import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { interpolate } from '@format/interpolate';
import { Button } from '@ui/button';
import { PricingList } from '../PricingList';
import { BenefitsOverview } from '../BenefitsOverview';
import { AnimatedLoader } from '../../components';
import { AddPupButton } from '../../molecules';
import { QUIZ_SUBPATH, PUP_INFO_SLUG } from '../routing';
import { HorizontalRule } from '../../atoms';
import { StarterBoxCheckoutSummary } from '../StarterBoxCheckoutSummary';
import { useReporter } from '../../reporter';
import { privacyPolicyClicked, tnCClicked } from './events';
import {
  StyledOrderSummary,
  AnimatedLoaderContainer,
  AddPetLine,
  ButtonContainer,
  TermsAndConditions,
  Header,
  AgreementsContainer,
  TermsAndConditionsLink,
  StyledCopy,
} from './styles.js';

export const OrderSummary = ({
  addPupBtnLabel,
  plans,
  pricingList,
  planSummary,
  onClickRemovePet,
  nextDeliveryDate,
  checkoutButton,
  termsAndConditions,
  buttonRef,
  disabled = false,
  isValidating = false,
  isSubmitting = false,
  recurringOrders,
  showAddons,
  period,
  showPromoCode,
  applyPromoCode,
  removePromoCode,
  isDefaultPromoCode,
  currentOfferCode,
}) => {
  const reporter = useReporter();

  if (isValidating) {
    return (
      <StyledOrderSummary>
        <AnimatedLoaderContainer>
          <AnimatedLoader message="Loading cart" show={true} />
        </AnimatedLoaderContainer>
      </StyledOrderSummary>
    );
  }

  const onTnCClicked = () => {
    reporter.tag(tnCClicked());
  };

  const onPrivacyPolicyClicked = () => {
    reporter.tag(privacyPolicyClicked());
  };

  return (
    <>
      <StyledOrderSummary>
        <Header>Order Summary</Header>
        <HorizontalRule />
        {plans.map((plan, idx) => {
          const avatarBgColor = idx % 2 ? 'hero.blueberry' : 'secondary.carrot';

          return (
            <React.Fragment key={plan.petId}>
              <StarterBoxCheckoutSummary
                avatarBgColor={avatarBgColor}
                breeds={plan.breeds}
                headline={interpolate(planSummary.headline, plan)}
                recipes={plan.recipes}
                productType={plan.productType}
                pricePerPeriod={plan.pricePerPeriod}
                pricePerPeriodWithDiscount={plan.pricePerPeriodWithDiscount}
                period={period}
                petId={plan.petId}
                onDeletePet={
                  plans.length > 1 ? () => onClickRemovePet(plan.petId) : null
                }
                nextDeliveryDate={nextDeliveryDate}
                numPacksCopy={interpolate(planSummary.numPacksCopy, plan)}
                bagCount={plan.bagCount}
                showAddons={showAddons}
                addons={plan.addons}
                portionSize={plan.portionSize}
                mealCount={plan.mealCount}
              />
              <HorizontalRule />
            </React.Fragment>
          );
        })}
        <AddPetLine>
          <AddPupButton
            href={`/onboarding/${QUIZ_SUBPATH}/${PUP_INFO_SLUG}/?pet_id=`}
            fullWidth={false}
            horizontalPadding={20}
            variant="blueberry"
            verticalPadding={1}
            text={addPupBtnLabel}
          />
        </AddPetLine>
        <HorizontalRule />
        <PricingList
          {...pricingList}
          plans={plans}
          showPromoCode={showPromoCode}
          applyPromoCode={applyPromoCode}
          removePromoCode={removePromoCode}
          isDefaultPromoCode={isDefaultPromoCode}
          currentOfferCode={currentOfferCode}
        />
        <ButtonContainer>
          <Button
            ref={buttonRef}
            disabled={disabled}
            variant="spinach"
            isLoading={isSubmitting}
          >
            {checkoutButton.text}
          </Button>
        </ButtonContainer>
        {recurringOrders.foodPacksText}
        <TermsAndConditions>
          <AgreementsContainer>
            <Link href="/terms" passHref legacyBehavior>
              <TermsAndConditionsLink onClick={onTnCClicked}>
                Terms and Conditions
              </TermsAndConditionsLink>
            </Link>
            <Link href="/privacy-policy" passHref legacyBehavior>
              <TermsAndConditionsLink onClick={onPrivacyPolicyClicked}>
                Privacy Policy
              </TermsAndConditionsLink>
            </Link>
          </AgreementsContainer>
        </TermsAndConditions>
      </StyledOrderSummary>
      <BenefitsOverview />
      <HorizontalRule width="40%" />
      <StyledCopy>
        {termsAndConditions}{' '}
        <Link href="mailto:caninecare@myollie.com" passHref legacyBehavior>
          <TermsAndConditionsLink>
            caninecare@myollie.com
          </TermsAndConditionsLink>
        </Link>
      </StyledCopy>
    </>
  );
};
OrderSummary.displayName = 'OrderSummary';
OrderSummary.propTypes = {
  addPupBtnLabel: PropTypes.string.isRequired,
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      breeds: PropTypes.arrayOf(PropTypes.string).isRequired,
      petId: PropTypes.string.isRequired,
      price: PropTypes.string.isRequired,
      priceWithDiscount: PropTypes.string.isRequired,
      recipes: PropTypes.object.isRequired,
      numPacks: PropTypes.number.isRequired,
      bagCount: PropTypes.number.isRequired,
      productType: PropTypes.string,
      addons: PropTypes.array,
    }),
  ).isRequired,
  onClickRemovePet: PropTypes.func.isRequired,
  nextDeliveryDate: PropTypes.string,
  pricingList: PropTypes.object.isRequired,
  planSummary: PropTypes.shape({
    headline: PropTypes.string.isRequired,
    numPacksCopy: PropTypes.string.isRequired,
    recipesLabel: PropTypes.string.isRequired,
  }).isRequired,
  checkoutButton: PropTypes.shape({
    text: PropTypes.string.isRequired,
  }).isRequired,
  // Rich text node
  termsAndConditions: PropTypes.node.isRequired,
  buttonRef: PropTypes.any,
  disabled: PropTypes.bool,
  isValidating: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  recurringOrders: PropTypes.shape({
    foodPacksText: PropTypes.string.isRequired,
  }).isRequired,
  period: PropTypes.string.isRequired,
  showAddons: PropTypes.bool.isRequired,
  showPromoCode: PropTypes.bool.isRequired,
  applyPromoCode: PropTypes.func.isRequired,
  removePromoCode: PropTypes.func.isRequired,
  isDefaultPromoCode: PropTypes.bool.isRequired,
  currentOfferCode: PropTypes.string.isRequired,
};
